import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';

interface ProductImportantInformationProps {
  data?: Record<string, string>;
}

const SECTION_ORDER = [
  'Safety Information',
  'Directions',
  'Ingredients',
  'Legal Disclaimer',
];

const ProductImportantInformation: React.FC<ProductImportantInformationProps> = ({ data }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | false>(false);

  if (!data || Object.keys(data).length === 0) return null;

  // Show SECTION_ORDER entries first, then any remaining keys
  const orderedKeys = [
    ...SECTION_ORDER.filter((k) => k in data),
    ...Object.keys(data).filter((k) => !SECTION_ORDER.includes(k)),
  ];

  const handleChange = (key: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? key : false);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {t('products.importantInformation', 'Important Information')}
      </Typography>

      {orderedKeys.map((key) => (
        <Accordion
          key={key}
          expanded={expanded === key}
          onChange={handleChange(key)}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px !important',
            mb: 1,
            '&:before': { display: 'none' },
            '&.Mui-expanded': { borderColor: 'primary.main' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              borderRadius: 2,
              minHeight: 48,
              '& .MuiAccordionSummary-content': { my: 1 },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {key}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.75, whiteSpace: 'pre-line' }}
            >
              {data[key]}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ProductImportantInformation;
